import React, {Suspense, lazy, useEffect, useState} from 'react'
import axios from 'axios'

const CallDocumentPreview = lazy(() => import('./CallDocumentPreview'))

const API_URL = process.env.REACT_APP_API_URL

export interface CallOpiDocument {
  id: number
  name: string
  path: string
  fileType: string
  fileSizeBytes?: number
  signedUrl?: string
}

interface CallDocumentsPanelProps {
  documents: CallOpiDocument[]
}

const fetchDocumentBlob = async (document: CallOpiDocument): Promise<Blob> => {
  const response = await axios.post(
    `${API_URL}/files/download`,
    {filePath: document.path},
    {responseType: 'blob'}
  )
  return new Blob([response.data], {type: 'application/pdf'})
}

const previewLoading = <div className='text-muted small py-3'>Loading document...</div>

const previewErrorMessages = {
  load: <div className='text-danger small py-3'>Unable to load this document. Please try again.</div>,
  render: (
    <div className='text-danger small py-3'>
      Unable to preview this document. Please use the download button instead.
    </div>
  ),
}

class PreviewErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean}> {
  state = {hasError: false}

  static getDerivedStateFromError() {
    return {hasError: true}
  }

  render() {
    return this.state.hasError ? previewErrorMessages.render : this.props.children
  }
}

export const CallDocumentsPanel: React.FC<CallDocumentsPanelProps> = ({documents}) => {
  const [previewDocument, setPreviewDocument] = useState<CallOpiDocument | null>(null)
  const [previewRequestId, setPreviewRequestId] = useState(0)
  const [previewFile, setPreviewFile] = useState<Blob | null>(null)
  const [previewError, setPreviewError] = useState<keyof typeof previewErrorMessages | null>(null)

  useEffect(() => {
    setPreviewFile(null)
    setPreviewError(null)
    if (!previewDocument) return

    let isCancelled = false
    fetchDocumentBlob(previewDocument)
      .then((file) => {
        if (!isCancelled) setPreviewFile(file)
      })
      .catch(() => {
        if (!isCancelled) setPreviewError('load')
      })
    return () => {
      isCancelled = true
    }
  }, [previewDocument, previewRequestId])

  const handlePreview = (document: CallOpiDocument) => {
    setPreviewDocument(document)
    setPreviewRequestId((requestId) => requestId + 1)
  }

  const handleDownload = async (document: CallOpiDocument) => {
    const response = await axios.post(
      `${API_URL}/files/download`,
      {filePath: document.path},
      {responseType: 'blob'}
    )
    const url = window.URL.createObjectURL(new Blob([response.data], {type: 'application/pdf'}))
    const link = window.document.createElement('a')
    link.href = url
    link.download = `${document.name || 'OPI Document'}.pdf`
    window.document.body.appendChild(link)
    link.click()
    window.document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  if (!documents.length) {
    return <div className='text-muted small py-3'>No documents available for this call.</div>
  }

  return (
    <div className='call-documents-panel'>
      <div className='call-documents-list border rounded'>
        {documents.map((document) => (
          <div
            key={document.id}
            className='call-document-item d-flex align-items-center justify-content-between p-3 border-bottom'
          >
            <div className='d-flex align-items-center gap-3'>
              <i className='bi bi-file-earmark-pdf-fill text-danger fs-2'></i>
              <div>
                <div className='fw-bold'>{document.name}</div>
                <div className='text-muted small'>PDF Document</div>
              </div>
            </div>
            <div className='d-flex align-items-center gap-2'>
              <button
                type='button'
                className='btn btn-sm btn-light border'
                onClick={() => handlePreview(document)}
              >
                <i className='bi bi-eye me-1'></i>
                Preview
              </button>
              <button
                type='button'
                className='btn btn-sm btn-light'
                onClick={() => handleDownload(document)}
                title='Download'
              >
                <i className='bi bi-download'></i>
              </button>
            </div>
          </div>
        ))}
      </div>

      {previewDocument && (
        <div className='call-document-preview mt-3 border rounded overflow-hidden'>
          <div className='d-flex justify-content-between align-items-center px-3 py-2 border-bottom bg-light'>
            <span className='fw-semibold small'>{previewDocument.name}</span>
            <button
              type='button'
              className='btn btn-sm btn-light'
              onClick={() => setPreviewDocument(null)}
            >
              Close Preview
            </button>
          </div>
          <div className='call-document-preview-body bg-light'>
            {previewError ? (
              previewErrorMessages[previewError]
            ) : previewFile ? (
              <PreviewErrorBoundary>
                <Suspense fallback={previewLoading}>
                  <CallDocumentPreview file={previewFile} onError={() => setPreviewError('render')} />
                </Suspense>
              </PreviewErrorBoundary>
            ) : (
              previewLoading
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default CallDocumentsPanel
